/**
 * AppShellHeader Component
 *
 * Sticky header for the authenticated app shell (sidebar + main area).
 * Used by AppShellLayout for all authenticated routes (dashboard, heroes, settings, etc.).
 * Contains:
 * - SidebarTrigger for toggling main sidebar
 * - Mobile logo
 * - Journey Instance Selector (desktop only)
 * - Language Selector, Theme Selector, CoPilot trigger, Feedback trigger
 * - Notification Bell, Profile button
 */

import { TonnieLogo } from '@tonnie/assets';
import { useTranslation } from '@tonnie/locale';
import { cn, SidebarTrigger } from '@tonnie/ui';

import { CoPilotTriggerButton } from '../ui/CoPilotTriggerButton';
import { LanguageSelector } from '../ui/LanguageSelector';
import { ThemeSelector } from '../ui/ThemeSelector';
import { UserMenu } from '../shared/UserMenu';
import { JourneyInstanceSelector } from '@/features/platform/journey/components';
import { FeedbackTriggerButton } from '@/features/platform/feedback/components';
import { InboxBell } from '@/features/platform/inbox/components/InboxBell';

// ═══════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════

export interface IAppShellHeaderProps {
  /** Callback to toggle mobile menu - deprecated, using SidebarTrigger now */
  onMobileMenuToggle?: () => void;
  /** Additional CSS classes */
  className?: string;
}

// ═══════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════

/**
 * App shell header with navigation controls and user actions
 */
export function AppShellHeader({ className }: IAppShellHeaderProps) {
  const { t } = useTranslation();
  return (
    <header
      className={cn(
        'h-16 border-b border-border bg-background/80 backdrop-blur-md',
        'flex items-center justify-between px-4 md:px-6',
        'sticky top-0 z-30 shrink-0',
        className
      )}
    >
      <div className="flex items-center gap-3">
        <SidebarTrigger
          className="hidden md:flex hover:bg-muted"
          toggleLabel={t('ui.sidebar.toggle')}
        />
        <TonnieLogo variant="symbol" size="sm" className="md:hidden" />
        <span className="font-bold text-lg md:hidden text-foreground">Tonnie</span>
        <JourneyInstanceSelector className="hidden lg:flex" />
      </div>

      <div className="flex items-center gap-2">
        <LanguageSelector />
        <ThemeSelector />
        <CoPilotTriggerButton />
        <FeedbackTriggerButton />
        <InboxBell />
        <UserMenu />
      </div>
    </header>
  );
}

export default AppShellHeader;
